// R-113: positive vitality damage wakes an unlocked sleeper after death/Dying settlement.
// Exact-row removal owns loss hooks, the first eligible same-target peer and a lone Concentrating source.
import { type JanusCtx, type Row, activeStatuses, ec, ecId, ecLean, handBack, insertRoll, record, scalar, upper, definitionNotHeld } from '../context.js';
import { runTriggerFamily } from '../trigger-executor.js';
import { statusItemId } from '../../statuses/expiry.js';
import { planStoredStatusRemoval, removeStoredStatus } from './status-effect-removal-helpers.js';
import { AuraOwnerGateUnavailable, isDormantOwnerGatedAura, isHostileAuraSelfAnchor, readStatusCasterId } from '../../compile/creature-status-reader.js';
import type { ItemFieldRow } from '../../compile/item-field-reader.js';

export { removeLoneConcentration } from './status-effect-removal-helpers.js';
export const SLEEPING = '095BC6BC-06F6-74E0-2B8A-FC1F32AF809B';
const LOCK = 'sleeping_not_ended_by_damage';
const present = (v: unknown): boolean => v !== undefined && v !== null && v !== '' && v !== 0 && v !== '0' && v !== false;

export interface WakePlan { target: string; statusIds: string[]; lossArm: boolean }

/** ft_creature_sleep_is_damage_locked: a flattened lock or an active payload status, using the shared aura filters. */
function sleepLocked(ctx: JanusCtx, target: Row): boolean {
  const value = (row: ItemFieldRow | null, key: string): unknown => ctx.read.value(row as Row | null, key);
  if (present(scalar(ctx.read.value(target, LOCK)))) return true;
  for (const status of activeStatuses(ctx, target)) {
    const def = ctx.read.definition(statusItemId(status)) ?? record(status.status_arr);
    if (!def) definitionNotHeld('apply_damage_to_target:wake:status-definition-not-held', statusItemId(status));
    if (!present(scalar(ctx.read.value(def, LOCK)))) continue;
    const holder = status.status_target_id ?? status.target ?? ecId(target);
    const caster = readStatusCasterId(status, holder);
    if (isHostileAuraSelfAnchor(def, status.status_aoe_source, caster, holder, value)) continue;
    try {
      const dormant = isDormantOwnerGatedAura({ ...status, status_arr: def, status_caster_id: caster }, value, id => {
        const owner = ctx.members.get(upper(id));
        if (!owner) handBack('apply_damage_to_target:wake:aura-owner-state-not-held');
        return { ...owner, encounter_creatures: { ...ec(owner), v_current: ecLean(owner, 'v_current') },
          statuses: { ...record(owner.statuses), target: activeStatuses(ctx, owner) } };
      }, target);
      if (!dormant) return true;
    } catch (error) {
      if (error instanceof AuraOwnerGateUnavailable) handBack('apply_damage_to_target:wake:aura-owner-state-not-held');
      throw error;
    }
  }
  return false;
}

/** State-only preparation. A missing sleeper or a live lock has no wake duty. */
export function planWake(ctx: JanusCtx, target: Row): WakePlan | null {
  const sleeping = activeStatuses(ctx, target).filter(s => statusItemId(s) === SLEEPING);
  if (!sleeping.length || sleepLocked(ctx, target)) return null;
  const removals = sleeping.map(status => planStoredStatusRemoval(ctx, target, status));
  return { target: ecId(target), statusIds: removals.map(plan => plan.statusId), lossArm: removals.some(plan => plan.lossArm) };
}

/** Callers enter only after positive vitality damage. Completed death already retired the rows; PC Dying still wakes normally. */
export function performWake(ctx: JanusCtx, plan: WakePlan, caster: Row, target: Row, lethal: boolean): void {
  if (lethal && ctx.composedDeaths?.includes(plan.target)) {
    // The stale damage caller retains its loss arm and log, without a second stored delete.
    if (plan.lossArm) runTriggerFamily(ctx, { trigger: 'triggers_when_caster_loses_status_effect', statusItemId: SLEEPING },
      target, null, [target], false, undefined, true);
  } else {
    for (const statusId of plan.statusIds) {
      const status = activeStatuses(ctx, target).find(s => upper(s.status_id) === statusId && statusItemId(s) === SLEEPING);
      if (status) removeStoredStatus(ctx, target, status, 'single');
    }
  }
  insertRoll(ctx, { roll_type_1: 'status_effect_removed_from_target_after_cast', roll_type_2: '', roll_item_id: SLEEPING,
    by: caster, to: target, roll_result: 0 });
}
